/**
 * APY calculation and formatting utilities
 */

import type { WebsiteConfig } from "../types/config";
import type { StakingStatsResponse } from "../types/protocol";

/**
 * YaaS fee percentage (Solomon takes 1.7% of yield)
 */
export const YAAS_FEE_PERCENT = 1.7;

/**
 * Default APY ceiling (22%) - used when config.APYCeiling is not set
 */
export const DEFAULT_APY_CEILING = 22;

/**
 * Calculate YaaS APY (base APY minus Solomon fee)
 *
 * @param baseApy - Base APY percentage (e.g., 14.5)
 * @returns YaaS APY after fee deduction, or null if invalid
 *
 * @example
 * calculateYaasApy(14.5) // 12.8 (14.5 - 1.7)
 */
export function calculateYaasApy(baseApy: number | null | undefined): number | null {
    if (baseApy === null || baseApy === undefined || isNaN(baseApy)) {
        return null;
    }
    return Math.max(0, baseApy - YAAS_FEE_PERCENT);
}

/**
 * Format YaaS APY for display
 *
 * @param baseApy - Base APY percentage
 * @returns Formatted YaaS APY string
 *
 * @example
 * formatYaasApy(14.5) // "12.8%"
 */
export function formatYaasApy(baseApy: number | null | undefined): string {
    const yaasApy = calculateYaasApy(baseApy);
    if (yaasApy === null) {
        return "-";
    }
    return `${yaasApy.toFixed(1)}%`;
}

/**
 * Format advertised APY for display
 * Handles various input formats and edge cases
 *
 * @param apy - APY value (number, string, null, undefined)
 * @returns Formatted APY string
 *
 * @example
 * formatAdvertisedApy(14.5) // "14.5%"
 * formatAdvertisedApy("14.5") // "14.5%"
 * formatAdvertisedApy(null) // "-"
 */
export function formatAdvertisedApy(apy: number | string | null | undefined): string {
    if (apy === null || apy === undefined) {
        return "-";
    }

    const num = typeof apy === "string" ? parseFloat(apy) : apy;

    if (isNaN(num)) {
        return "-";
    }

    return `${num.toFixed(1)}%`;
}

/**
 * Converts a value to a number, returning undefined if invalid.
 */
function toNumberOrUndefined(value: unknown): number | undefined {
    if (value === undefined || value === null) return undefined;
    const num = typeof value === "string" ? parseFloat(value) : Number(value);
    return Number.isFinite(num) ? num : undefined;
}

/**
 * Resolves the advertised APY using waterfall logic:
 *
 * 1. If APYOverride is set in config -> use that (decimal format, e.g., 0.128 = 12.8%)
 * 2. Else use currentVestingInfo.sevenDayApy if > 0
 * 3. Fallback to previousVestingInfo.sevenDayApy
 * 4. Convert from decimal (0.127) to percentage (12.7)
 * 5. If result > APYCeiling -> clamp to ceiling
 *
 * Note: All config values (APYOverride, APYCeiling) and sevenDayApy from stats
 * are expected in decimal format (e.g., 0.22 = 22%).
 *
 * @param config - Website config with optional APYOverride and APYCeiling (decimal format)
 * @param stats - Staking stats response from data API
 * @returns APY as a percentage number (e.g., 12.7), or null if unavailable
 *
 * @example
 * ```tsx
 * const apy = resolveAdvertisedApy(websiteConfig, stakingStats);
 * console.log(`APY: ${apy}%`); // "APY: 12.7%"
 * ```
 */
export function resolveAdvertisedApy(config: WebsiteConfig = {}, stats?: StakingStatsResponse): number | null {
    // Get ceiling from config (decimal format) or use default (already in percentage)
    const configCeiling = toNumberOrUndefined(config.APYCeiling);
    const ceiling = configCeiling !== undefined ? configCeiling * 100 : DEFAULT_APY_CEILING;

    // Step 1: Check for APYOverride in config (decimal format)
    const apyOverride = toNumberOrUndefined(config.APYOverride);
    if (apyOverride !== undefined && apyOverride > 0) {
        // Convert APYOverride from decimal to percentage and apply ceiling
        const overridePercentage = apyOverride * 100;
        return Math.min(overridePercentage, ceiling);
    }

    // Step 2 & 3: Waterfall from stats
    if (!stats) return null;

    const currentApy = toNumberOrUndefined(stats.currentVestingInfo?.sevenDayApy);
    const previousApy = toNumberOrUndefined(stats.previousVestingInfo?.sevenDayApy);

    // Use current APY only if it's a positive value; zero or negative falls back to previous
    let chosen: number | undefined;
    if (currentApy !== undefined && currentApy > 0) {
        chosen = currentApy;
    } else if (previousApy !== undefined && previousApy > 0) {
        chosen = previousApy;
    }

    if (chosen === undefined) return null;

    // Step 4: Convert from decimal (0.127) to percentage (12.7)
    const percentage = chosen * 100;

    // Step 5: Apply ceiling cap
    return Math.min(percentage, ceiling);
}
